import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {FormService} from "@core/shared/services/form.service";
import {environment} from "../../../../../environments/environment";
import {UserService} from "@core/shared/services/user.service";
import {RegistrationAffiliation} from "@core/shared/models/registration-affiliation";
import {RegistrationAffiliationService} from "@core/shared/services/registration-affiliation.service";
import {FileConfig} from "@lib/form/fields/file/file.component";
import {ApiService} from "@core/shared/services/api.service";

@Component({
    selector: 'app-core-page-registration-affiliation-update',
    templateUrl: './page-registration-affiliation-update.component.html',
    styleUrls: ['./page-registration-affiliation-update.component.scss'],
    providers: [
        FormService
    ],
    standalone: false
})
export class PageRegistrationAffiliationUpdateComponent implements OnInit {

    public registrationAffiliation: RegistrationAffiliation;

    public registrationLogoConfig: FileConfig;

    public administrationLogoConfig: FileConfig;

    public allowedSubscriptions: {id: string, label: string}[] = [
        {
            id: 'starter',
            label: 'subscription.pack.starter.plural.value'
        },
        {
            id: 'premium',
            label: 'subscription.pack.premium.plural.value'
        }
    ];

    public constructor(
        private _formBuilder: UntypedFormBuilder,
        private _snackBar: MatSnackBar,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _translateService: TranslateService,
        private _userService: UserService,
        private _registrationAffiliationService: RegistrationAffiliationService,
        private _apiService: ApiService,
        public formService: FormService
    ) {}

    ngOnInit(): void {

        this._initForm();

        this._activatedRoute.data.subscribe((data: { registrationAffiliation: RegistrationAffiliation }): void => {

            this.registrationAffiliation = data.registrationAffiliation;

            this._hydrateForm(data.registrationAffiliation);
        });
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            name: ['', Validators.required],
            comment: [''],
            allowedSubscriptions: [[], [(control: UntypedFormControl) => {

                if (control.value.length >= 1) {

                    return null;
                }

                return {
                    'minLengthArray': {
                        valid: false
                    }
                };
            }]],
            registrationLogo: [null],
            administrationLogo: [null]
        });

        this.formService.submitCallback = (): void => {

            const data: object = Object.assign(this.form.value, {});

            this._registrationAffiliationService.updateItemAPI(this.registrationAffiliation.id, data).subscribe((): void => {

                this._snackBar.open(this._translateService.instant('registrationAffiliation.edit.success.value'), this._translateService.instant('notification.close.action.value'), {
                    duration: 5000
                });

                this.redirectToList();
            });
        };

        this._initRegistrationLogoConfigs();

        this._initAdministrationLogoConfigs();
    }

    private _hydrateForm(item: RegistrationAffiliation): void {

        this.form.patchValue(item);
    }

    private _initRegistrationLogoConfigs(): void {

        this._translateService.get('file.extension.list.allowed.value', {list: '.png, .jpeg, .svg'}).subscribe((help: string): void => {

            this.registrationLogoConfig = {
                id: 'registrationLogo',
                gallery: {
                    id: null,
                    type: 'file',
                    context: 'registration_affiliation_registration_logo'
                },
                uploadMaxFileSize: 100000,
                required: false,
                uploadApiUrl: this._apiService.getApiUrl(false, true)
            };
        });
    }

    private _initAdministrationLogoConfigs(): void {

        this._translateService.get('file.extension.list.allowed.value', {list: '.png, .jpeg, .svg'}).subscribe((help: string): void => {

            this.administrationLogoConfig = {
                id: 'administrationLogo',
                gallery: {
                    id: null,
                    type: 'file',
                    context: 'registration_affiliation_administration_logo'
                },
                uploadMaxFileSize: 100000,
                required: false,
                uploadApiUrl: this._apiService.getApiUrl(false, true)
            };
        });
    }

    public redirectToList(): void {

        this._router.navigate(['account/relationship/list'], { queryParams: { targetTab: 'affiliation'}});
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get registrationIdentifierUrl(): string {

        return `${environment.registerUrl}/${this._userService.currentUser.getValue().locale}/registration/role-choice?registrationAffiliation=${this.registrationAffiliation.uniqueIdentifier}`;
    }

    get loginIdentifierUrl(): string {

        return `${window.location.origin}/${this._userService.currentUser.getValue().locale}/signin?registrationAffiliation=${this.registrationAffiliation.uniqueIdentifier}`;
    }
}
